/**
 * Composable for "drag on existing track line to insert a new
 * route point" in track edit mode.
 *
 * While the user holds & drags on the visible track line:
 * 1. Figure out which route-point interval (between rp[i] and rp[i+1]) was grabbed
 * 2. Show a rubber-band preview: two straight lines rp[i]→cursor and cursor→rp[i+1]
 * 3. On release, call the supplied onInsert(insertIndex, lat, lon) callback
 *
 * Real routing only happens on drop; during drag we show straight-line previews.
 */
import maplibregl from 'maplibre-gl'
import { onUnmounted, ref, watch, type Ref, type ShallowRef } from 'vue'

import { buildLineStringFromSegments, parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { useUiStore } from '@/stores/uiStore'
import type { Tour } from '@/types/tour'
import { getTrackLineInsertionProjection } from '@/utils/trackLineInsertion'
import { getTrackRoutePoints } from '@/utils/trackRoutePoints'
import { Z_INDEX } from '@/utils/zIndex'

interface Options {
  mapInstance: ShallowRef<maplibregl.Map | null> | Ref<maplibregl.Map | null>
  /** Layer ID whose mousedown/touchstart events start the drag. Null disables the composable. */
  getTouchTargetLayerId: () => string | null
  /** The tour currently being edited, or null. */
  getEditingTour: () => Tour | null
  /** Index of the track within the tour being edited, or null. */
  getEditingTrackIndex: () => number | null
  /** Called when the user drops the drag. `insertIndex` is track-local. */
  onInsert: (insertIndex: number, lat: number, lon: number) => Promise<void> | void
}

const RUBBER_BAND_SOURCE_ID = 'track-edit-rubber-band-source'
const RUBBER_BAND_LAYER_ID = 'track-edit-rubber-band-layer'

/** Squared planar distance between two [lon, lat] pairs (fine for local comparisons). */
function distSq(a: [number, number], b: [number, number]): number {
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  return dx * dx + dy * dy
}

/**
 * For each route point, find the index of the closest polyline vertex.
 * This partitions the polyline into intervals between consecutive RP anchors.
 */
function findRouteAnchorIndices(
  polyline: [number, number][],
  routePoints: { lat: number; lon: number }[],
): number[] {
  const indices: number[] = []
  for (const rp of routePoints) {
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < polyline.length; i++) {
      const d = distSq(polyline[i], [rp.lon, rp.lat])
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    indices.push(bestIdx)
  }
  return indices
}

/**
 * Find which "RP interval" contains the given hit position.
 * Returns the insertIndex (1..routePoints.length-1) — i.e. the new route point
 * would be inserted before routePoints[insertIndex].
 */
function findInsertIndex(
  polyline: [number, number][],
  anchorIndices: number[],
  hit: [number, number],
): number {
  if (polyline.length === 0 || anchorIndices.length < 2) return 1

  // Find the polyline vertex closest to the hit point
  let nearestIdx = 0
  let bestDist = Infinity
  for (let i = 0; i < polyline.length; i++) {
    const d = distSq(polyline[i], hit)
    if (d < bestDist) {
      bestDist = d
      nearestIdx = i
    }
  }

  // Find which interval [anchorIndices[i], anchorIndices[i+1]] contains nearestIdx
  for (let i = 0; i < anchorIndices.length - 1; i++) {
    const start = Math.min(anchorIndices[i], anchorIndices[i + 1])
    const end = Math.max(anchorIndices[i], anchorIndices[i + 1])
    if (nearestIdx >= start && nearestIdx <= end) {
      return i + 1
    }
  }

  // Fallback: clamp to last interval
  return anchorIndices.length - 1
}

export function useTrackEditInsertDrag(options: Options) {
  const { mapInstance } = options
  const uiStore = useUiStore()

  const isDragging = ref(false)
  const dragStartedAt = ref(0)
  let insertIndex = 0
  let anchorA: [number, number] | null = null // [lon, lat]
  let anchorB: [number, number] | null = null
  let livePivotLineString: string | null = null
  let dragMarker: maplibregl.Marker | null = null
  let activeLayerId: string | null = null
  let lastDragEndTime = 0
  let pendingWindowEndCancelId: number | null = null

  let cleanupFns: (() => void)[] = []

  const clearPendingWindowEndCancel = () => {
    if (pendingWindowEndCancelId !== null) {
      clearTimeout(pendingWindowEndCancelId)
      pendingWindowEndCancelId = null
    }
  }

  const resetDragState = (map: maplibregl.Map | null) => {
    clearPendingWindowEndCancel()
    if (map) {
      map.dragPan.enable()
      map.getCanvas().style.cursor = ''
      removeRubberBand(map)
    }
    removeDragMarker()
    isDragging.value = false
    insertIndex = 0
    anchorA = null
    anchorB = null
    livePivotLineString = null
  }

  const cancelDrag = () => {
    if (!isDragging.value) return
    resetDragState(mapInstance.value)
  }

  const removeRubberBand = (map: maplibregl.Map) => {
    try {
      if (map.getLayer(RUBBER_BAND_LAYER_ID)) map.removeLayer(RUBBER_BAND_LAYER_ID)
      if (map.getSource(RUBBER_BAND_SOURCE_ID)) map.removeSource(RUBBER_BAND_SOURCE_ID)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing rubber band', { error })
    }
  }

  const updateRubberBand = (map: maplibregl.Map, cursor: [number, number]) => {
    if (!anchorA || !anchorB) return
    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [anchorA, cursor, anchorB],
          },
        },
      ],
    }
    const src = map.getSource(RUBBER_BAND_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
    if (src) {
      src.setData(geojson)
    } else {
      map.addSource(RUBBER_BAND_SOURCE_ID, { type: 'geojson', data: geojson })
      map.addLayer({
        id: RUBBER_BAND_LAYER_ID,
        type: 'line',
        source: RUBBER_BAND_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#3B82F6',
          'line-width': 3,
          'line-dasharray': [2, 2],
        },
      })
    }
  }

  const createDragMarker = (map: maplibregl.Map, lngLat: maplibregl.LngLat) => {
    removeDragMarker()
    const el = document.createElement('div')
    el.style.width = '16px'
    el.style.height = '16px'
    el.style.borderRadius = '50%'
    el.style.backgroundColor = '#3B82F6'
    el.style.border = '3px solid #ffffff'
    el.style.boxShadow = '0 0 6px rgba(0,0,0,0.4)'
    el.style.pointerEvents = 'none'
    el.style.zIndex = String(Z_INDEX.MAP_MARKERS)
    dragMarker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(map)
  }

  const removeDragMarker = () => {
    if (dragMarker) {
      dragMarker.remove()
      dragMarker = null
    }
  }

  const startDrag = (map: maplibregl.Map, lngLat: maplibregl.LngLat, point?: maplibregl.Point) => {
    const tour = options.getEditingTour()
    const trackIdx = options.getEditingTrackIndex()
    if (!tour || trackIdx === null || trackIdx < 0) return

    const track = tour.tracks[trackIdx]
    const trackLineString =
      track?.pointsLineReduced5m ??
      (track ? buildLineStringFromSegments(track.segments) : undefined)
    if (!track || !trackLineString) return
    const trackRoutePoints = getTrackRoutePoints(tour.routePoints ?? [], track.clientId)
    if (trackRoutePoints.length < 2) return

    // Don't start drag if the pointer is near an existing route point marker —
    // clicks/taps on markers should open their context menu instead.
    const hitPoint = point ?? map.project(lngLat)
    const MARKER_HIT_RADIUS_PX = 24
    for (const rp of tour.routePoints) {
      const rpPoint = map.project([rp.lon, rp.lat])
      const dx = rpPoint.x - hitPoint.x
      const dy = rpPoint.y - hitPoint.y
      if (dx * dx + dy * dy <= MARKER_HIT_RADIUS_PX * MARKER_HIT_RADIUS_PX) {
        return
      }
    }

    const polyline = parseLineString(trackLineString)
    if (polyline.length === 0) return

    const anchors = findRouteAnchorIndices(polyline, trackRoutePoints)
    const hit: [number, number] = [lngLat.lng, lngLat.lat]
    insertIndex = findInsertIndex(polyline, anchors, hit)
    if (insertIndex < 1 || insertIndex > trackRoutePoints.length - 1) return

    const rpBefore = trackRoutePoints[insertIndex - 1]
    const rpAfter = trackRoutePoints[insertIndex]
    const localPreview =
      trackRoutePoints.length === 2
        ? getTrackLineInsertionProjection(trackLineString, lngLat.lat, lngLat.lng)
        : null
    anchorA = localPreview?.segmentStart ?? [rpBefore.lon, rpBefore.lat]
    anchorB = localPreview?.segmentEnd ?? [rpAfter.lon, rpAfter.lat]
    livePivotLineString = trackRoutePoints.length === 2 ? trackLineString : null

    isDragging.value = true
    dragStartedAt.value = Date.now()
    map.dragPan.disable()
    map.getCanvas().style.cursor = 'grabbing'

    createDragMarker(map, lngLat)
    updateRubberBand(map, hit)
  }

  const moveDrag = (map: maplibregl.Map, lngLat: maplibregl.LngLat) => {
    if (!isDragging.value) return
    if (dragMarker) dragMarker.setLngLat(lngLat)
    if (livePivotLineString) {
      const preview = getTrackLineInsertionProjection(livePivotLineString, lngLat.lat, lngLat.lng)
      if (preview) {
        anchorA = preview.segmentStart
        anchorB = preview.segmentEnd
      }
    }
    updateRubberBand(map, [lngLat.lng, lngLat.lat])
  }

  const endDrag = async (map: maplibregl.Map, lngLat: maplibregl.LngLat) => {
    if (!isDragging.value) return
    const capturedIndex = insertIndex
    lastDragEndTime = Date.now()
    resetDragState(map)
    try {
      await options.onInsert(capturedIndex, lngLat.lat, lngLat.lng)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Track edit insert drag onInsert failed', { error })
    }
  }

  const attachListeners = (map: maplibregl.Map, layerId: string) => {
    detachListeners()
    activeLayerId = layerId

    const onMouseDown = (e: maplibregl.MapLayerMouseEvent) => {
      if (!uiStore.isTrackEditMode) return
      // Check marker proximity first; only preventDefault if we actually start a drag.
      const before = isDragging.value
      startDrag(map, e.lngLat, e.point)
      if (!before && isDragging.value) {
        e.preventDefault()
        e.originalEvent.stopPropagation()
      }
    }
    const onTouchStart = (e: maplibregl.MapLayerTouchEvent) => {
      if (!uiStore.isTrackEditMode) return
      if (e.originalEvent.touches.length !== 1) return
      const before = isDragging.value
      startDrag(map, e.lngLat, e.point)
      if (!before && isDragging.value) {
        e.preventDefault()
        e.originalEvent.stopPropagation()
      }
    }
    const onMouseMove = (e: maplibregl.MapMouseEvent) => moveDrag(map, e.lngLat)
    const onTouchMove = (e: maplibregl.MapTouchEvent) => {
      if (!isDragging.value) return
      moveDrag(map, e.lngLat)
    }
    const onMouseUp = (e: maplibregl.MapMouseEvent) => {
      if (!isDragging.value) return
      clearPendingWindowEndCancel()
      void endDrag(map, e.lngLat)
    }
    const onTouchEnd = (e: maplibregl.MapTouchEvent) => {
      if (!isDragging.value) return
      clearPendingWindowEndCancel()
      void endDrag(map, e.lngLat)
    }
    const onWindowMouseUp = () => {
      cancelDrag()
    }
    const onWindowTouchEnd = () => {
      cancelDrag()
    }
    const onWindowBlur = () => {
      cancelDrag()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        cancelDrag()
      }
    }
    const onLayerMouseEnter = () => {
      if (!isDragging.value && uiStore.isTrackEditMode) {
        map.getCanvas().style.cursor = 'grab'
      }
    }
    const onLayerMouseLeave = () => {
      if (!isDragging.value) map.getCanvas().style.cursor = ''
    }

    map.on('mousedown', layerId, onMouseDown)
    map.on('touchstart', layerId, onTouchStart)
    map.on('mouseenter', layerId, onLayerMouseEnter)
    map.on('mouseleave', layerId, onLayerMouseLeave)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    map.on('touchmove', onTouchMove)
    map.on('touchend', onTouchEnd)
    window.addEventListener('mouseup', onWindowMouseUp)
    window.addEventListener('touchend', onWindowTouchEnd)
    window.addEventListener('touchcancel', onWindowTouchEnd)
    window.addEventListener('blur', onWindowBlur)
    document.addEventListener('visibilitychange', onVisibilityChange)

    cleanupFns = [
      () => map.off('mousedown', layerId, onMouseDown),
      () => map.off('touchstart', layerId, onTouchStart),
      () => map.off('mouseenter', layerId, onLayerMouseEnter),
      () => map.off('mouseleave', layerId, onLayerMouseLeave),
      () => map.off('mousemove', onMouseMove),
      () => map.off('mouseup', onMouseUp),
      () => map.off('touchmove', onTouchMove),
      () => map.off('touchend', onTouchEnd),
      () => window.removeEventListener('mouseup', onWindowMouseUp),
      () => window.removeEventListener('touchend', onWindowTouchEnd),
      () => window.removeEventListener('touchcancel', onWindowTouchEnd),
      () => window.removeEventListener('blur', onWindowBlur),
      () => document.removeEventListener('visibilitychange', onVisibilityChange),
    ]
  }

  const detachListeners = () => {
    clearPendingWindowEndCancel()
    cleanupFns.forEach((fn) => {
      try {
        fn()
      } catch {
        // listener may already be gone if the layer was removed
      }
    })
    cleanupFns = []
    activeLayerId = null
    const map = mapInstance.value
    if (map) {
      resetDragState(map)
    }
    isDragging.value = false
  }

  const trySetup = () => {
    const map = mapInstance.value
    if (!map) return
    if (!uiStore.isTrackEditMode) {
      detachListeners()
      return
    }
    const layerId = options.getTouchTargetLayerId()
    if (!layerId) {
      detachListeners()
      return
    }
    if (!map.getLayer(layerId)) {
      // Layer may be added asynchronously; retry on next sourcedata event
      const onSourceData = () => {
        if (map.getLayer(layerId)) {
          map.off('sourcedata', onSourceData)
          attachListeners(map, layerId)
        }
      }
      map.on('sourcedata', onSourceData)
      cleanupFns.push(() => map.off('sourcedata', onSourceData))
      return
    }
    if (activeLayerId !== layerId) {
      attachListeners(map, layerId)
    }
  }

  watch(
    [mapInstance, () => uiStore.isTrackEditMode, () => options.getTouchTargetLayerId()],
    () => {
      trySetup()
    },
    { immediate: true },
  )

  onUnmounted(() => {
    detachListeners()
  })

  /** Returns true if a drag just ended (within 300ms) — use to suppress map click. */
  const shouldSuppressClick = () => Date.now() - lastDragEndTime < 300

  return {
    isDragging,
    shouldSuppressClick,
  }
}
